import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SensorType, type SensorReading, type DeviceInfo } from '@grosync/common';
import { AdminTopBar, ViewScroll, Button, Panel, Sym } from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import { fetchRecentAlerts } from '../../api/sensors';
import { fetchDevices } from '../../api/devices';

/* ============================================================================
 * 알림 / 이상치 로그 — 웹 UI (Panel + 그리드 테이블, 다른 콘솔 화면과 통일)
 * 실데이터: 최근 24h 센서 이상치(fetchRecentAlerts). anomaly 수준으로 경고/위험 구분.
 * 읽음 상태는 백엔드 미지원이라 클라이언트 로컬에서만 관리한다.
 * ========================================================================= */

type Level = 'warn' | 'crit';

interface LevelMeta {
  label: string;
  icon: string;
  c: string;
  bg: string;
}
const LEVEL_META: Record<Level, LevelMeta> = {
  crit: {
    label: '위험',
    icon: 'error',
    c: 'var(--md-sys-color-error)',
    bg: 'var(--md-sys-color-error-container)',
  },
  warn: {
    label: '경고',
    icon: 'warning',
    c: 'var(--gs-warning)',
    bg: 'var(--gs-warning-container)',
  },
};

const SENSOR_LABEL: Partial<Record<SensorType, string>> = {
  [SensorType.TEMPERATURE]: '온도',
  [SensorType.HUMIDITY]: '습도',
  [SensorType.CO2]: 'CO₂',
  [SensorType.ILLUMINANCE]: '조도',
  [SensorType.SOIL_MOISTURE]: '토양수분',
  [SensorType.SOIL_TEMPERATURE]: '지온',
  [SensorType.EC]: 'EC',
  [SensorType.PH]: 'pH',
  [SensorType.ATMOSPHERIC_PRESSURE]: '기압',
  [SensorType.UV_INDEX]: '자외선',
  [SensorType.WIND_SPEED]: '풍속',
  [SensorType.WIND_DIRECTION]: '풍향',
  [SensorType.RAINFALL]: '강우량',
  [SensorType.LEAF_WETNESS]: '엽면수분',
};
const SENSOR_ICON: Partial<Record<SensorType, string>> = {
  [SensorType.TEMPERATURE]: 'thermostat',
  [SensorType.HUMIDITY]: 'water_drop',
  [SensorType.CO2]: 'co2',
  [SensorType.ILLUMINANCE]: 'light_mode',
  [SensorType.SOIL_MOISTURE]: 'humidity_mid',
  [SensorType.SOIL_TEMPERATURE]: 'device_thermostat',
  [SensorType.EC]: 'bolt',
  [SensorType.PH]: 'science',
  [SensorType.WIND_SPEED]: 'air',
  [SensorType.RAINFALL]: 'rainy',
};

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return '방금 전';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  return `${Math.floor(h / 24)}일 전`;
}

/** ISO → "MM.DD HH:mm" */
function fmtAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface AlertItem {
  id: string;
  iso: string;
  ago: string;
  icon: string;
  sensorLabel: string;
  deviceName: string;
  valueText: string;
  level: Level;
}

function buildItems(readings: SensorReading[], devices: DeviceInfo[]): AlertItem[] {
  const nameOf = new Map(devices.map((d) => [d.id, d.name]));
  return [...readings]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .map((r) => ({
      id: r.id,
      iso: r.timestamp,
      ago: timeAgo(r.timestamp),
      icon: SENSOR_ICON[r.sensorType] ?? 'warning',
      sensorLabel: SENSOR_LABEL[r.sensorType] ?? r.sensorType,
      deviceName: nameOf.get(r.deviceId) ?? r.deviceId,
      valueText: `${r.value}${r.unit}`,
      level: r.anomaly === 'critical' ? 'crit' : 'warn',
    }));
}

type FilterKey = 'ALL' | Level;

const COLS = '110px minmax(0, 2.2fr) 0.9fr 1fr 92px';

export default function AlertsView(): React.ReactElement {
  const [filter, setFilter] = useState<FilterKey>('ALL');
  const [readIds, setReadIds] = useState<Set<string>>(new Set());

  const alertsQuery = useQuery({
    queryKey: ['recent-alerts'],
    queryFn: fetchRecentAlerts,
    refetchInterval: 60_000,
  });
  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });

  const items = useMemo(
    () => buildItems(alertsQuery.data ?? [], devicesQuery.data ?? []),
    [alertsQuery.data, devicesQuery.data],
  );

  const counts: Record<FilterKey, number> = {
    ALL: items.length,
    crit: items.filter((i) => i.level === 'crit').length,
    warn: items.filter((i) => i.level === 'warn').length,
  };
  const unread = items.filter((i) => !readIds.has(i.id)).length;
  const list = filter === 'ALL' ? items : items.filter((i) => i.level === filter);

  const markAllRead = (): void => setReadIds(new Set(items.map((i) => i.id)));
  const toggleRead = (id: string): void => setReadIds((prev) => new Set(prev).add(id));

  const FILTERS: { key: FilterKey; label: string; color: string }[] = [
    { key: 'ALL', label: '전체', color: 'var(--md-sys-color-on-surface-variant)' },
    { key: 'warn', label: LEVEL_META.warn.label, color: LEVEL_META.warn.c },
    { key: 'crit', label: LEVEL_META.crit.label, color: LEVEL_META.crit.c },
  ];

  const loading = alertsQuery.isLoading;

  return (
    <>
      <AdminTopBar title="알림" subtitle={`이상치 ${items.length}건 · 미확인 ${unread}건`} />

      <ViewScroll gap={18}>
        <ContentToolbar
          actions={
            <Button variant="outlined" icon="done_all" onClick={markAllRead}>
              모두 읽음
            </Button>
          }
          filters={FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                style={{
                  height: 38,
                  padding: '0 12px',
                  borderRadius: 9999,
                  cursor: 'pointer',
                  border: active ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                  background: active ? 'var(--md-sys-color-secondary-container)' : 'transparent',
                  color: active
                    ? 'var(--md-sys-color-on-secondary-container)'
                    : 'var(--md-sys-color-on-surface-variant)',
                  font: `${active ? 600 : 500} 13px/1 var(--gs-font-kr)`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background 150ms',
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: f.color }} />
                {f.label}
                <span className="gs-num" style={{ fontWeight: 700 }}>
                  {counts[f.key]}
                </span>
              </button>
            );
          })}
        />

        <Panel title="이상치 알림" subtitle="최근 24시간" pad={false}>
          <div className="gs-scroll-hide" style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 720 }}>
              {/* header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  padding: '12px 22px',
                  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-low)',
                }}
              >
                {['심각도', '항목 · 장비', '현재값', '발생 시각', '상태'].map((h, i) => (
                  <span
                    key={h}
                    style={{
                      font: '600 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      textAlign: i === 2 || i === 4 ? 'right' : 'left',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* rows */}
              {loading ? (
                <EmptyRow text="불러오는 중…" />
              ) : list.length === 0 ? (
                <EmptyRow
                  text={
                    filter === 'ALL'
                      ? '최근 24시간 내 이상치가 없습니다'
                      : `${filter === 'crit' ? '위험' : '경고'} 알림이 없습니다`
                  }
                />
              ) : (
                list.map((n, i) => {
                  const m = LEVEL_META[n.level];
                  const read = readIds.has(n.id);
                  const last = i === list.length - 1;
                  return (
                    <div
                      key={n.id}
                      className="gs-row"
                      onClick={() => toggleRead(n.id)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: COLS,
                        padding: '13px 22px',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderBottom: last ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                        background: read ? 'transparent' : 'var(--md-sys-color-surface-container-low)',
                      }}
                    >
                      {/* 심각도 */}
                      <span>
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            height: 22,
                            padding: '0 9px',
                            borderRadius: 9999,
                            background: m.bg,
                            color: m.c,
                            font: '600 11px/1 var(--gs-font-kr)',
                          }}
                        >
                          <Sym name={m.icon} size={12} fill={1} />
                          {m.label}
                        </span>
                      </span>

                      {/* 항목 · 장비 */}
                      <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0, paddingRight: 12 }}>
                        <span
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: 10,
                            flexShrink: 0,
                            background: m.bg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Sym name={n.icon} size={18} fill={1} style={{ color: m.c }} />
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span
                            style={{
                              display: 'block',
                              font: '600 13px/17px var(--gs-font-kr)',
                              color: 'var(--md-sys-color-on-surface)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {n.sensorLabel} 이상치
                          </span>
                          <span
                            style={{
                              display: 'block',
                              font: '400 12px/16px var(--gs-font-kr)',
                              color: 'var(--md-sys-color-on-surface-variant)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {n.deviceName}
                          </span>
                        </span>
                      </span>

                      {/* 현재값 */}
                      <span
                        className="gs-num"
                        style={{
                          font: '600 14px/18px var(--gs-font-kr)',
                          color: 'var(--md-sys-color-on-surface)',
                          textAlign: 'right',
                        }}
                      >
                        {n.valueText}
                      </span>

                      {/* 발생 시각 */}
                      <span style={{ paddingLeft: 16 }}>
                        <span
                          className="gs-num"
                          style={{
                            display: 'block',
                            font: '500 12px/16px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface)',
                          }}
                        >
                          {fmtAt(n.iso)}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            font: '400 11px/15px var(--gs-font-kr)',
                            color: 'var(--md-sys-color-on-surface-variant)',
                          }}
                        >
                          {n.ago}
                        </span>
                      </span>

                      {/* 상태 */}
                      <span style={{ textAlign: 'right' }}>
                        {read ? (
                          <span style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                            읽음
                          </span>
                        ) : (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5,
                              font: '600 12px/16px var(--gs-font-kr)',
                              color: 'var(--md-sys-color-primary)',
                            }}
                          >
                            <span style={{ width: 7, height: 7, borderRadius: 9999, background: 'var(--md-sys-color-primary)' }} />
                            미확인
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Panel>
      </ViewScroll>
    </>
  );
}

function EmptyRow({ text }: { text: string }): React.ReactElement {
  return (
    <div
      style={{
        padding: '40px 22px',
        textAlign: 'center',
        font: '400 13px/18px var(--gs-font-kr)',
        color: 'var(--md-sys-color-on-surface-variant)',
      }}
    >
      {text}
    </div>
  );
}
